import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  targetDate: string; // Format: dd/mm/yyyy
};

export const CountdownTimer = ({ targetDate }: Props) => {
  const { translations, language } = useLanguage();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Get translations with English fallback
  const t = (key: string, fallback: string) => {
    return translations?.timeline?.countdown?.[key] || fallback;
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const [day, month, year] = targetDate.split('/').map(Number);
      const target = new Date(year, month - 1, day);
      const now = new Date();
      const difference = target.getTime() - now.getTime();

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const timeUnits = [
    { value: timeLeft.days, label: t('days', 'Days') },
    { value: timeLeft.hours, label: t('hours', 'Hours') },
    { value: timeLeft.minutes, label: t('minutes', 'Minutes') },
    { value: timeLeft.seconds, label: t('seconds', 'Seconds') }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100"
    >
      <div className="flex items-center gap-2 sm:gap-3 mb-4">
        <div className="p-2 bg-primary-50 rounded-lg">
          <Clock className="text-primary-500 w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <h3 className="font-semibold text-base sm:text-lg text-gray-900">
          {t('title', 'Time Remaining')}
        </h3>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        {timeUnits.map((unit, index) => (
          <motion.div
            key={unit.label}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className="flex flex-col items-center"
          >
            <div className="w-full h-16 sm:w-16 sm:h-16 bg-primary-50 rounded-lg flex items-center justify-center mb-1 sm:mb-2">
              <span className="text-xl sm:text-2xl font-bold text-primary-600">
                {String(unit.value).padStart(2, '0')}
              </span>
            </div>
            <span className="text-xs sm:text-sm text-gray-600">{unit.label}</span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};